import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Package, Plus, Search, Filter, Star, Eye, ShoppingCart, TrendingUp, DollarSign, AlertTriangle, CheckCircle, Minus, RotateCcw, Briefcase } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { AuthenticatedImage } from '@/components/AuthenticatedImage'
import { ProductImage } from '@/components/MultipleImageUpload'
import { apiClient } from '@/services/api'
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate'

interface Product {
  id: string
  name: string
  type: string
  description?: string
  materialCost: number
  laborCost: number
  totalCost: number
  suggestedSellingPrice: number
  actualSellingPrice?: number
  profitMargin?: number
  totalLaborHours: number
  imageUrls: string[]
  defaultImage?: ProductImage
  isActive: boolean
  isFeatured: boolean
  timesReordered: number
  quantityInStock: number
  quantitySold: number
  lastSoldPrice?: number
  lastSoldDate?: string
  customerName?: string
  completedDate: string
  createdAt: string
}

interface ProductFilters {
  search: string
  type: string
  isActive: boolean | null
  isFeatured: boolean | null
  sortBy: string
  sortOrder: 'ASC' | 'DESC'
}

export const ProductsListPage: React.FC = () => {
  const { template: currentTemplate, loading: templateLoading } = useCurrentTemplate()
  const [products, setProducts] = useState<Product[]>([])
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([])
  const [productCategories, setProductCategories] = useState<Array<{id: string, name: string}>>([])
  const [filters, setFilters] = useState<ProductFilters>({
    search: '',
    type: '',
    isActive: null,
    isFeatured: null,
    sortBy: 'createdAt',
    sortOrder: 'DESC'
  })
  const [isLoading, setIsLoading] = useState(true)
  const [stats, setStats] = useState({
    totalProducts: 0,
    activeProducts: 0,
    featuredProducts: 0,
    totalValue: 0,
    averageProfit: 0
  })
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set())
  const [showBulkSale, setShowBulkSale] = useState(false)
  const [showSellModal, setShowSellModal] = useState<string | null>(null)
  const [sellQuantity, setSellQuantity] = useState(1)
  const [sellPrice, setSellPrice] = useState('')

  useEffect(() => {
    fetchProducts()
    fetchStats()
    fetchProductCategories()
  }, [])

  useEffect(() => {
    applyFilters()
  }, [products, filters])

  const fetchProducts = async () => {
    try {
      const response = await apiClient.get('/products')
      console.log('🔍 Products API response:', response)
      
      let productsData: Product[] = []
      
      // Handle the correct response structure from backend
      if (response && typeof response === 'object') {
        if (response.products && Array.isArray(response.products)) {
          // Expected structure: { products: [], total: number, ... }
          productsData = response.products
        } else if (Array.isArray(response)) {
          // Direct array response
          productsData = response
        } else {
          console.warn('⚠️ Unexpected products response structure:', response)
          productsData = []
        }
      } else {
        console.warn('⚠️ Invalid products response:', response)
        productsData = []
      }

      // Fetch default images for each product
      const productsWithDefaultImages = await Promise.all(
        productsData.map(async (product) => {
          try {
            const defaultImageResponse = await apiClient.get(`/products/${product.id}/images/default`)
            return {
              ...product,
              defaultImage: defaultImageResponse || null
            }
          } catch (error) {
            // If no default image or error, use fallback
            console.warn(`No default image for product ${product.id}:`, error)
            return {
              ...product,
              defaultImage: null
            }
          }
        })
      )

      setProducts(productsWithDefaultImages)
    } catch (error) {
      console.error('❌ Error fetching products:', error)
      setProducts([])
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/products/stats')
      console.log('📊 Products stats API response:', response)
      
      if (response && typeof response === 'object') {
        setStats({
          totalProducts: response.totalProducts || 0,
          activeProducts: response.activeProducts || 0,
          featuredProducts: response.featuredProducts || 0,
          totalValue: response.totalValue || 0,
          averageProfit: response.averageProfit || 0
        })
      } else {
        console.warn('⚠️ Invalid stats response:', response)
        setStats({
          totalProducts: 0,
          activeProducts: 0,
          featuredProducts: 0,
          totalValue: 0,
          averageProfit: 0
        })
      }
    } catch (error) {
      console.error('❌ Error fetching stats:', error)
      setStats({
        totalProducts: 0,
        activeProducts: 0,
        featuredProducts: 0,
        totalValue: 0,
        averageProfit: 0
      })
    }
  }

  const fetchProductCategories = async () => {
    try {
      const response = await apiClient.get('/templates/user/form-config')
      // Filter for product categories from the form config
      const allCategories = response?.categories || response?.data?.categories || []
      const productCategories = allCategories.filter((cat: any) => cat.categoryType === 'product')
      setProductCategories(productCategories)
    } catch (error) {
      console.error('Error fetching product categories:', error)
    }
  }

  const applyFilters = () => {
    let filtered = [...products]

    // Search filter
    if (filters.search) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(filters.search.toLowerCase()) ||
        product.description?.toLowerCase().includes(filters.search.toLowerCase()) ||
        product.customerName?.toLowerCase().includes(filters.search.toLowerCase())
      )
    }

    // Type filter
    if (filters.type) {
      filtered = filtered.filter(product => product.type === filters.type)
    }

    // Active filter
    if (filters.isActive !== null) {
      filtered = filtered.filter(product => product.isActive === filters.isActive)
    }

    // Featured filter
    if (filters.isFeatured !== null) {
      filtered = filtered.filter(product => product.isFeatured === filters.isFeatured)
    }

    // Sorting
    filtered.sort((a, b) => {
      let aValue: any = a[filters.sortBy as keyof Product]
      let bValue: any = b[filters.sortBy as keyof Product]

      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (filters.sortOrder === 'ASC') {
        return aValue > bValue ? 1 : -1
      } else {
        return aValue < bValue ? 1 : -1
      }
    })

    setFilteredProducts(filtered)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const handleSellProduct = async (productId: string, quantity: number, price: number) => {
    try {
      const response = await apiClient.post(`/products/${productId}/mark-sold`, {
        quantity,
        salePrice: price,
        saleDate: new Date().toISOString()
      })
      
      // Update local state
      setProducts(prev => prev.map(product =>
        product.id === productId ? response.data : product
      ))
      
      setShowSellModal(null)
      setSellQuantity(1)
      setSellPrice('')
    } catch (error) {
      console.error('Error selling product:', error)
    }
  }

  const handleBulkSale = async () => {
    try {
      // For bulk sale, we'd need to call the mark-sold endpoint for each selected product
      // This is a simplified implementation - in a real app you might want a dedicated bulk endpoint

      // Process each selected product (this is a simplified version)
      for (const productId of selectedProducts) {
        await apiClient.post(`/products/${productId}/mark-sold`, {
          quantity: 1, // Default quantity - in real app this would come from form
          salePrice: 0, // Default price - in real app this would come from form
          saleDate: new Date().toISOString()
        })
      }

      // Refresh products after bulk sale
      await fetchProducts()
      
      setShowBulkSale(false)
      setSelectedProducts(new Set())
    } catch (error) {
      console.error('Error with bulk sale:', error)
    }
  }

  const handleReorderProduct = async (productId: string) => {
    try {
      const response = await apiClient.post(`/products/${productId}/reorder`, {
        quantity: 1, // Default quantity for reorder
        notes: 'Reordered from products list'
      })
      
      // Update local state to increment times reordered
      setProducts(prev => prev.map(product =>
        product.id === productId
          ? { ...product, timesReordered: product.timesReordered + 1 }
          : product
      ))

      // Show availability info if there are missing materials
      if (!response.data.availability.available) {
        alert(`Cannot complete reorder. Missing materials: ${response.data.availability.missingMaterials.map((m: any) => m.materialName).join(', ')}`)
      }
    } catch (error) {
      console.error('Error reordering product:', error)
    }
  }

  const toggleProductSelection = (productId: string) => {
    setSelectedProducts(prev => {
      const newSet = new Set(prev)
      if (newSet.has(productId)) {
        newSet.delete(productId)
      } else {
        newSet.add(productId)
      }
      return newSet
    })
  }

  const getStockStatus = (product: Product) => {
    if (product.quantityInStock === 0) {
      return { status: 'out-of-stock', color: 'text-red-600', icon: AlertTriangle }
    } else if (product.quantityInStock <= 2) {
      return { status: 'low-stock', color: 'text-yellow-600', icon: AlertTriangle }
    } else {
      return { status: 'in-stock', color: 'text-green-600', icon: CheckCircle }
    }
  }

  const getTemplateDescription = () => {
    if (!currentTemplate) return "Manage your completed products and catalog"
    
    switch (currentTemplate.businessType) {
      case '3d_printing':
        return "Manage your completed 3D prints and product catalog"
      case 'jewelry':
        return "Manage your completed jewelry pieces and product catalog"
      case 'woodworking':
        return "Manage your completed woodworking projects and product catalog"
      case 'photography':
        return "Manage your completed photo products and portfolio"
      case 'automotive':
        return "Manage your completed automotive projects and parts catalog"
      case 'electronics':
        return "Manage your completed electronic projects and component catalog"
      case 'culinary':
        return "Manage your completed culinary products and recipe catalog"
      default:
        return `Manage your completed ${currentTemplate.name} products and catalog`
    }
  }

  if (isLoading || templateLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Package className="w-12 h-12 mx-auto mb-4 text-gray-400 animate-pulse" />
          <p className="text-gray-600 dark:text-gray-400">Loading products...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Products
            </h1>
            {currentTemplate && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <Briefcase className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {currentTemplate.name}
                </span>
              </div>
            )}
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            {getTemplateDescription()}
          </p>
        </div>
        <div className="flex gap-3">
          {selectedProducts.size > 0 && (
            <Button
              variant="outline"
              leftIcon={<DollarSign className="w-4 h-4" />}
              onClick={() => setShowBulkSale(true)}
            >
              Sell Selected ({selectedProducts.size})
            </Button>
          )}
          <Link to="/products/create-from-project">
            <Button variant="outline" leftIcon={<Plus className="w-4 h-4" />}>
              Create from Project
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="p-4">
          <div className="flex items-center">
            <Package className="w-8 h-8 text-blue-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Products</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalProducts}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <Eye className="w-8 h-8 text-green-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.activeProducts}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <Star className="w-8 h-8 text-yellow-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Featured</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.featuredProducts}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <TrendingUp className="w-8 h-8 text-purple-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Value</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{formatCurrency(stats.totalValue)}</p>
            </div>
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center">
            <ShoppingCart className="w-8 h-8 text-indigo-600" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Profit</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.averageProfit}%</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search products..."
              value={filters.search}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                setFilters(prev => ({ ...prev, search: e.target.value }))
              }
              className="pl-10"
            />
          </div>
          <Select
            value={filters.type}
            onValueChange={(value) => setFilters(prev => ({ ...prev, type: value }))}
          >
            <option value="">All Types</option>
            {productCategories.map((category) => (
              <option key={category.id} value={category.name.toLowerCase()}>
                {category.name}
              </option>
            ))}
          </Select>
          <Select
            value={filters.isActive === null ? '' : filters.isActive.toString()}
            onValueChange={(value) => 
              setFilters(prev => ({ 
                ...prev, 
                isActive: value === '' ? null : value === 'true' 
              }))
            }
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </Select>
          <Select
            value={filters.isFeatured === null ? '' : filters.isFeatured.toString()}
            onValueChange={(value) => 
              setFilters(prev => ({ 
                ...prev, 
                isFeatured: value === '' ? null : value === 'true' 
              }))
            }
          >
            <option value="">All Featured</option>
            <option value="true">Featured</option>
            <option value="false">Not Featured</option>
          </Select>
          <Select
            value={filters.sortBy}
            onValueChange={(value) => setFilters(prev => ({ ...prev, sortBy: value }))}
          >
            <option value="createdAt">Date Created</option>
            <option value="name">Name</option>
            <option value="totalCost">Total Cost</option>
            <option value="suggestedSellingPrice">Selling Price</option>
            <option value="timesReordered">Times Reordered</option>
          </Select>
          <Select
            value={filters.sortOrder}
            onValueChange={(value) => setFilters(prev => ({ ...prev, sortOrder: value as 'ASC' | 'DESC' }))}
          >
            <option value="DESC">Descending</option>
            <option value="ASC">Ascending</option>
          </Select>
        </div>
      </Card>

      {/* Products Grid */}
      {filteredProducts.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <Package className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No products found
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {filters.search || filters.type ? 
                'Try adjusting your filters to see more products.' :
                'Create your first product from a completed project.'
              }
            </p>
            <Link to="/products/create-from-project">
              <Button leftIcon={<Plus className="w-4 h-4" />}>
                Create from Project
              </Button>
            </Link>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const stockStatus = getStockStatus(product)
            const StockIcon = stockStatus.icon
            
            return (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative bg-gray-100 dark:bg-gray-800">
                  {product.defaultImage ? (
                    <div className="relative">
                      <AuthenticatedImage
                        src={product.defaultImage.imageUrl}
                        alt={product.defaultImage.altText || product.name}
                        className="w-full h-48 object-cover relative z-0"
                        fallback={
                          <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                            <Package className="w-12 h-12 text-gray-400" />
                          </div>
                        }
                      />
                      {/* Default image indicator */}
                      <div className="absolute bottom-2 left-2 bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                        <Star className="w-3 h-3 fill-current" />
                        Default
                      </div>
                    </div>
                  ) : product.imageUrls.length > 0 ? (
                    // Fallback to old imageUrls system
                    <AuthenticatedImage
                      src={product.imageUrls[0]}
                      alt={product.name}
                      className="w-full h-48 object-cover relative z-0"
                      fallback={
                        <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                          <Package className="w-12 h-12 text-gray-400" />
                        </div>
                      }
                    />
                  ) : (
                    <div className="w-full h-48 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
                      <Package className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                  
                  {/* Selection checkbox for bulk operations */}
                  <div className="absolute top-2 left-2 z-10">
                    <input
                      type="checkbox"
                      checked={selectedProducts.has(product.id)}
                      onChange={() => toggleProductSelection(product.id)}
                      className="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500 shadow-sm"
                    />
                  </div>
                  
                  {/* Stock status indicator */}
                  <div className={`absolute top-2 right-2 z-10 flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 shadow-sm ${stockStatus.color}`}>
                    <StockIcon className="w-3 h-3" />
                    <span className="text-xs font-medium">{product.quantityInStock}</span>
                  </div>
                </div>
                
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {product.name}
                    </h3>
                    <div className="flex gap-1">
                      {product.isFeatured && (
                        <Badge variant="warning" size="sm">
                          <Star className="w-3 h-3 mr-1" />
                          Featured
                        </Badge>
                      )}
                      {!product.isActive && (
                        <Badge variant="secondary" size="sm">
                          Inactive
                        </Badge>
                      )}
                    </div>
                  </div>
                
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                  {product.description}
                </p>
                
                {/* Inventory Information */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mb-3">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">In Stock:</span>
                      <span className={`font-medium ${stockStatus.color}`}>{product.quantityInStock}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Sold:</span>
                      <span className="font-medium">{product.quantitySold}</span>
                    </div>
                    {product.lastSoldPrice && (
                      <div className="flex justify-between col-span-2">
                        <span className="text-gray-600 dark:text-gray-400">Last Sale:</span>
                        <span className="font-medium">{formatCurrency(product.lastSoldPrice)}</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Material Cost:</span>
                    <span className="font-medium">{formatCurrency(product.materialCost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Labor Cost:</span>
                    <span className="font-medium">{formatCurrency(product.laborCost)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t pt-2">
                    <span>Suggested Price:</span>
                    <span className="text-green-600">{formatCurrency(product.suggestedSellingPrice)}</span>
                  </div>
                  {product.actualSellingPrice && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Actual Price:</span>
                      <span className="font-medium">{formatCurrency(product.actualSellingPrice)}</span>
                    </div>
                  )}
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-4">
                  <span>{product.totalLaborHours}h labor</span>
                  <span>Reordered {product.timesReordered}x</span>
                  <span>{formatDate(product.completedDate)}</span>
                </div>
                
                <div className="grid grid-cols-3 gap-2">
                  <Link to={`/products/${product.id}`}>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      View
                    </Button>
                  </Link>
                  <Button
                    size="sm"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => setShowSellModal(product.id)}
                    disabled={product.quantityInStock === 0}
                  >
                    <ShoppingCart className="w-4 h-4 mr-1 text-white" />
                    Buy
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    onClick={() => handleReorderProduct(product.id)}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Reorder
                  </Button>
                </div>
              </div>
            </Card>
            )
          })}
        </div>
      )}

      {/* Sell Product Modal */}
      {showSellModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Sell Product to Customer</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Quantity</label>
                <Input
                  type="number"
                  min="1"
                  max={products.find(p => p.id === showSellModal)?.quantityInStock || 1}
                  value={sellQuantity}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellQuantity(parseInt(e.target.value) || 1)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Sale Price</label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={sellPrice}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSellPrice(e.target.value)}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSellModal(null)
                    setSellQuantity(1)
                    setSellPrice('')
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    if (showSellModal && sellPrice) {
                      handleSellProduct(showSellModal, sellQuantity, parseFloat(sellPrice))
                    }
                  }}
                  disabled={!sellPrice}
                  className="flex-1"
                >
                  Sell
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Sale Modal */}
      {showBulkSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl">
            <h3 className="text-lg font-semibold mb-4">Bulk Sale</h3>
            <div className="space-y-4">
              <div className="max-h-60 overflow-y-auto">
                {Array.from(selectedProducts).map(productId => {
                  const product = products.find(p => p.id === productId)
                  if (!product) return null
                  
                  return (
                    <div key={productId} className="flex items-center justify-between p-3 border rounded-lg">
                      <div>
                        <h4 className="font-medium">{product.name}</h4>
                        <p className="text-sm text-gray-600">Stock: {product.quantityInStock}</p>
                      </div>
                      <div className="flex gap-2">
                        <Input
                          type="number"
                          min="1"
                          max={product.quantityInStock}
                          defaultValue="1"
                          className="w-20"
                        />
                        <Input
                          type="number"
                          step="0.01"
                          placeholder="Price"
                          className="w-24"
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowBulkSale(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleBulkSale}
                  className="flex-1"
                >
                  Process Sales
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}